import React, { useState } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { LazyLoadImage } from "react-lazy-load-image-component";
import FormInput from "../Input/formInput";
import SupplierProductSelectionModal from "./SupplierProductSelectionModal";
import showErrorToast, { showSuccessToast } from "../toast/toast";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import swal from "sweetalert";

const SupplierProductSelector = ({
  selectedProducts,
  setSelectedProducts,
  setLoading,
  compID,
  supplierID,
  onSaveRelations,
  onProductUpdated,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [updatingProductId, setUpdatingProductId] = useState(null); // Track which product is being updated

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  // Handle modal submit - save relations to database
  const handleModalSubmit = async (newSelectedProducts) => {
    if (onSaveRelations) {
      const success = await onSaveRelations(newSelectedProducts);
      if (success) {
        setSelectedProducts(newSelectedProducts);
      }
      return success;
    } else {
      // Fallback to just setting state if no save function provided
      setSelectedProducts(newSelectedProducts);
      return true;
    }
  };

  // Remove product from database and local state
  const removeProduct = async (productId) => {
    // Find the product to get its details
    const product = selectedProducts.find(p => p.menuItem_ID === productId);
    if (!product) return;

    // Check if product has an ID (exists in database)
    if (!product.ID || product.ID === 0 || product.ID === "0") {
      // Just remove from local state if it doesn't exist in database
      setSelectedProducts(selectedProducts.filter(p => p.menuItem_ID !== productId));
      return;
    }

    // Show confirmation dialog
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to remove "${product.menuItem_Name}" from this supplier's products?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) return;

    try {
      setUpdatingProductId(productId); // Show loading state

      // Call delete API
      await axiosInstance.delete(APIs.DELETE_SUPPLIER_PRODUCT_RELATION, {
        params: { id: product.ID, compID: compID }
      });

      // Remove from local state after successful deletion
      setSelectedProducts(selectedProducts.filter(p => p.menuItem_ID !== productId));

      // Notify parent component
      if (onProductUpdated) {
        const updatedProducts = selectedProducts.filter(p => p.menuItem_ID !== productId);
        onProductUpdated(updatedProducts);
      }

      showSuccessToast(`"${product.menuItem_Name}" removed successfully`);

    } catch (error) {
      console.error('Error deleting product relation:', error);
      showErrorToast(`Failed to remove "${product.menuItem_Name}"`);
    } finally {
      setUpdatingProductId(null);
    }
  };

  // Update product price
  const updateProductPrice = (productId, price) => {
    setSelectedProducts(selectedProducts.map(product =>
      product.menuItem_ID === productId
        ? { ...product, Supplier_Price: price }
        : product
    ));
  };

  // Toggle preferred supplier
  const togglePreferred = (productId) => {
    setSelectedProducts(selectedProducts.map(product =>
      product.menuItem_ID === productId
        ? { ...product, Is_Preferred: !product.Is_Preferred }
        : product
    ));
  };

  // Update individual product relation
  const updateProductRelation = async (product) => {
    if (!compID || !supplierID) return;

    console.log("Updating product:", product); // Debug log

    // Check if product has a valid ID
    if (!product.ID || product.ID === 0 || product.ID === "0") {
      showErrorToast(`Cannot update ${product.menuItem_Name} - please save changes first using the modal`);
      return;
    }

    try {
      setUpdatingProductId(product.menuItem_ID); // Set loading for this specific product

      // Create payload for single product update
      const updatePayload = {
        ID: product.ID,
        CompID: compID,
        SupplierID: supplierID,
        MenuItem_ID: product.menuItem_ID,
        Supplier_Price: parseFloat(product.Supplier_Price || 0),
        Is_Preferred: product.Is_Preferred || false,
        Is_Active: product.Is_Active !== undefined ? product.Is_Active : true
      };

      console.log("Update payload:", updatePayload); // Debug log

      // Call the update API
      await axiosInstance.put(APIs.PUT_UPDATE_SUPPLIER_PRODUCT_RELATION, updatePayload);

      showSuccessToast(`${product.menuItem_Name} updated successfully`);

      // Update the local state to reflect the changes
      const updatedProducts = selectedProducts.map(p =>
        p.menuItem_ID === product.menuItem_ID ? { ...p, ...updatePayload } : p
      );
      setSelectedProducts(updatedProducts);

      // Notify parent component of the update
      if (onProductUpdated) {
        onProductUpdated(updatedProducts);
      }

    } catch (error) {
      console.error('Error updating product relation:', error);
      showErrorToast(`Failed to update ${product.menuItem_Name}`);
    } finally {
      setUpdatingProductId(null); // Clear loading state
    }
  };

  const renderProductItem = (product) => {
    return (
      <div
        key={product.menuItem_ID}
        className="product-chip"
        style={{
          background: isDarkMode ? "#3d4447" : "#ffffff",
          borderRadius: "12px",
          padding: "16px 20px",
          border: isDarkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #e9ecef",
          transition: "all 0.2s ease",
          cursor: "default",
          position: "relative",
          boxShadow: isDarkMode ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.05)",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "#007bff";
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 123, 255, 0.15)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = isDarkMode ? "rgba(255, 255, 255, 0.2)" : "#e9ecef";
          e.currentTarget.style.boxShadow = isDarkMode ? "0 2px 8px rgba(0, 0, 0, 0.3)" : "0 2px 8px rgba(0, 0, 0, 0.05)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div
              style={{
                width: "36px",
                height: "36px",
                background: isDarkMode ? "#2d3436" : "#f8f9fa",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginRight: "12px",
                border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid #e9ecef",
                overflow: "hidden",
              }}
            >
              {product.menuItem_Image ? (
                <LazyLoadImage
                  src={product.menuItem_Image}
                  alt={product.menuItem_Name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "6px",
                  }}
                />
              ) : (
                <i className="fa fa-box" style={{ fontSize: "14px", color: isDarkMode ? "#adb5bd" : "#6c757d" }}></i>
              )}
            </div>
            <div>
              <span style={{ fontSize: "15px", fontWeight: "500", color: isDarkMode ? "#ffffff" : "#495057" }}>
                {product.menuItem_Name}
              </span>
              {product.menuItem_Type && (
                <div style={{ fontSize: "12px", color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                  Type: {product.menuItem_Type}
                </div>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            {/* Price Input */}
            <div style={{ width: "100px" }}>
              <FormInput
                type="number"
                step="0.01"
                placeHolder="0.00"
                value={product.Supplier_Price || ""}
                onchange={(e) => updateProductPrice(product.menuItem_ID, e.target.value)}
                style={{ fontSize: "14px", padding: "6px 8px" }}
              />
            </div>

            {/* Preferred Toggle */}
            <div className="d-flex align-items-center">
              <input
                type="checkbox"
                checked={product.Is_Preferred || false}
                onChange={() => togglePreferred(product.menuItem_ID)}
                style={{ marginRight: "4px" }}
              />
              <small style={{ color: isDarkMode ? "#ffffff" : "#6c757d" }}>Preferred</small>
            </div>

            {/* Update Button */}
            <button
              onClick={() => updateProductRelation(product)}
              disabled={updatingProductId === product.menuItem_ID || !product.ID || product.ID === 0 || product.ID === "0"}
              title={!product.ID || product.ID === 0 || product.ID === "0" ? "Please save changes using the modal first" : "Update this product relation"}
              style={{
                background: (!product.ID || product.ID === 0 || product.ID === "0") ? "#6c757d" : themeColor,
                border: `1px solid ${(!product.ID || product.ID === 0 || product.ID === "0") ? "#6c757d" : themeColor}`,
                borderRadius: "6px",
                width: "60px",
                height: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: (updatingProductId === product.menuItem_ID || !product.ID || product.ID === 0 || product.ID === "0") ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                color: "white",
                fontSize: "11px",
                fontWeight: "500",
                marginRight: "8px",
                opacity: (updatingProductId === product.menuItem_ID || !product.ID || product.ID === 0 || product.ID === "0") ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (updatingProductId !== product.menuItem_ID) {
                  e.currentTarget.style.background = `${themeColor}dd`;
                  e.currentTarget.style.transform = "translateY(-1px)";
                }
              }}
              onMouseLeave={(e) => {
                if (updatingProductId !== product.menuItem_ID) {
                  e.currentTarget.style.background = themeColor;
                  e.currentTarget.style.transform = "translateY(0)";
                }
              }}
            >
              {updatingProductId === product.menuItem_ID ? <i className="fa fa-spinner fa-spin"></i> :
               (!product.ID || product.ID === 0 || product.ID === "0") ? "Save First" : "Update"}
            </button>

            {/* Remove Button */}
            <button
              onClick={() => removeProduct(product.menuItem_ID)}
              disabled={updatingProductId === product.menuItem_ID}
              title="Remove this product from supplier"
              style={{
                background: "transparent",
                border: "none",
                borderRadius: "6px",
                width: "28px",
                height: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: updatingProductId === product.menuItem_ID ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                color: updatingProductId === product.menuItem_ID ? "#6c757d" : "#dc3545",
                opacity: updatingProductId === product.menuItem_ID ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (updatingProductId !== product.menuItem_ID) {
                  e.currentTarget.style.background = isDarkMode ? "rgba(220, 53, 69, 0.2)" : "#f8d7da";
                  e.currentTarget.style.color = isDarkMode ? "#ff6b7a" : "#721c24";
                }
              }}
              onMouseLeave={(e) => {
                if (updatingProductId !== product.menuItem_ID) {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#dc3545";
                }
              }}
            >
              {updatingProductId === product.menuItem_ID ? (
                <i className="fa fa-spinner fa-spin" style={{ fontSize: "10px" }}></i>
              ) : (
                <i className="fa fa-times" style={{ fontSize: "12px" }}></i>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Selected Products Section */}
      {selectedProducts.length > 0 && (
        <div className="mb-4">
          <h6 className="mb-3" style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '600' }}>
            <i className="fa fa-check-circle me-2" style={{ color: themeColor }}></i>
            Selected Products ({selectedProducts.length})
          </h6>
          <div className="row row-cols-1 g-3">
            {selectedProducts.map(product => renderProductItem(product))}
          </div>
        </div>
      )}

      {/* Add Products Section */}
      <div className="d-flex justify-content-center">
        <button
          onClick={() => setShowModal(true)}
          style={{
            backgroundColor: themeColor,
            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
            border: `1px solid ${themeColor}`,
            boxShadow: `0 4px 15px ${themeColor}40`,
            borderRadius: "8px",
            padding: "12px 24px",
            color: "white",
            fontSize: "14px",
            fontWeight: "500",
            cursor: "pointer",
            transition: "all 0.2s ease",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = `0 6px 20px ${themeColor}60`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = `0 4px 15px ${themeColor}40`;
          }}
        >
          <i className="fa-solid fa-plus" style={{ fontSize: "14px" }}></i>
          {selectedProducts.length > 0 ? "Add More Products" : "Select Products"}
        </button>
      </div>

      {/* Product Selection Modal */}
      <SupplierProductSelectionModal
        showModal={showModal}
        setShowModal={setShowModal}
        onSubmit={handleModalSubmit}
        currentSelectedProducts={selectedProducts}
      />
    </div>
  );
};

export default SupplierProductSelector;